import {
	Component,
	Input,
	OnInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	EventEmitter,
	Output,
	SimpleChanges,
	OnChanges,
	ViewChild,
	ElementRef,
	OnDestroy
} from '@angular/core';
import { BaseInputComponent } from '../base-input/base-input.component';
import { compareWithSomething } from '../../../../_core/state/global/global.utils';
import { LocaleDto } from '../../../../../../../api/src/creative-unit-package/models/locale.dto';
import { takeUntil } from 'rxjs';

@Component({
	selector: 'app-select-input',
	templateUrl: './select-input.component.html',
	styleUrls: ['./select-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectInputComponent extends BaseInputComponent implements OnInit, OnChanges, OnDestroy {
	@Input() italicPlaceholder: string | null;
	@Output() openedChange = new EventEmitter<boolean>();

	@Input() showLocale: boolean = false;
	@Input() prefix: string;
	@Input() localeData: LocaleDto;
	@Input() showSearch: boolean = false;
	@Input() searchKeys: string[]; // Keys to search on the options, if not provided will search on the option itself
	@Input() sublineKey: string; // Key for the subline text in the options
	@Input() multiple: boolean = false;
	@Output() localeChange = new EventEmitter<string>();

	public compareWithSomething = compareWithSomething;
	public selectedOption: any;

	public searchString: string = '';
	@ViewChild('searchInput', { static: false }) searchInput: ElementRef;

	constructor(private cdr: ChangeDetectorRef) {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();
		// Subscribe to form value changes to update selectedOption
		// The form is already initialized by super.ngOnInit(), so we can set up the subscription immediately
		if (this.form) {
			this.form.get(this.controlName)?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
				this.updateSelectedOption(value);
			});
			// Update selectedOption initially after form is initialized
			this.updateSelectedOption(this.form.get(this.controlName)?.value);
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	private updateSelectedOption(value: any): void {
		if (!this.optionValueKey || !this.options) {
			return;
		}

		if (this.multiple) {
			const values = value || [];
			if (Array.isArray(values) && values.length > 0) {
				this.selectedOption = this.options.filter(option =>
					values.includes(option[this.optionValueKey])
				);
			} else {
				this.selectedOption = [];
			}
		} else {
			if (value !== undefined && value !== null) {
				this.selectedOption = this.options.find(option =>
					option && option[this.optionValueKey] === value
				);
			} else {
				this.selectedOption = null;
			}
		}
		this.cdr.markForCheck();
	}

	// Use arrow function to maintain 'this' context when called from template
	public searchFn = (option: any, searchEnabled: boolean, searchString: string, optionLabelKey: string, searchAttributes: string[]): boolean => {
		if (!searchEnabled) {
			return true;
		}

		// Check if this option is currently selected - if so, always show it
		try {
			let currentValue: any = null;
			if (this.form && this.controlName) {
				const control = this.form.get(this.controlName);
				if (control) {
					currentValue = control.value;
				}
			}
			if (currentValue === null && this.currentValue !== undefined) {
				currentValue = this.currentValue;
			}

			if (currentValue !== undefined && currentValue !== null) {
				let isSelected = false;

				if (this.multiple && Array.isArray(currentValue)) {
					const optionValue = this.optionValueKey ? option[this.optionValueKey] : option;
					isSelected = currentValue.some(selected => {
						if (this.optionValueKey) {
							return this.compareWithSomething(selected, optionValue);
						}
						return this.compareWithSomething(selected, option);
					});
				} else {
					const optionValue = this.optionValueKey ? option[this.optionValueKey] : option;
					if (this.optionValueKey) {
						isSelected = this.compareWithSomething(currentValue, optionValue);
					} else {
						isSelected = this.compareWithSomething(currentValue, option);
					}
				}

				// If selected, always show regardless of search
				if (isSelected) {
					return true;
				}
			}
		} catch (error) {
			// If error checking selection, continue with normal search filter
		}

		// Apply search filter to non-selected items
		const term = (searchString ?? '').toString().toLowerCase();
		if (!term) {
			return true;
		}

		const parts: string[] = [];

		// Specific attributes to search
		if (searchAttributes?.length && option) {
			for (const attr of searchAttributes) {
				const v = option?.[attr];
				if (v !== undefined && v !== null) {
					parts.push(String(v));
				}
			}
		}

		// Preferred label key
		if (optionLabelKey && option?.[optionLabelKey] != null) {
			parts.push(String(option[optionLabelKey]));
		}

		// Common label-ish fallbacks
		const fallback = option?.label ?? option?.name ?? option?.title ?? option?.value ?? option?.id;
		if (fallback != null) {
			parts.push(String(fallback));
		}

		// Primitive option fallback
		if (option !== null && option !== undefined && typeof option !== 'object') {
			parts.push(String(option));
		}

		const toSearch = parts.join(' ').toLowerCase();
		if (!toSearch) {
			return true;
		}

		return toSearch.includes(term);
	};

	ngOnChanges(changes: SimpleChanges): void {
		super.ngOnChanges(changes);

		// Update selectedOption when optionValueKey is used and options change
		if (this.optionValueKey && this.options) {
			// Get current value from form or currentValue input
			const formValue = this.form?.get(this.controlName)?.value;
			const value = formValue !== undefined ? formValue : this.currentValue;
			this.updateSelectedOption(value);
		}
	}

	getLocale(value: string, localeData: any): string {
		let locale = window.navigator.language;
		if (localStorage.getItem('localeCode')) {
			locale = localStorage.getItem('localeCode');
		}

		// See if the localeData has a value for the current locale.
		if (localeData && localeData[locale]) {
			return localeData[locale];
		} else {
			return value;
		}
	}

	getOptionLabel(option: any): string {
		// Get the base label using the same fallback logic as the template
		let label = option.label || option.name || option.id || option[this.optionLabelKey || 'id'] || option;

		// If label is an empty string or object, just show the ID
		if (!label || label === '' || typeof label === 'object') {
			const id = option.id || option[this.optionValueKey] || 'no id';
			return String(id);
		}

		// Convert to string and trim
		label = String(label).trim();

		// If after trimming it's empty, just show the ID
		if (!label) {
			const id = option.id || option[this.optionValueKey] || 'no id';
			return String(id);
		}

		return label;
	}

	getSelectedDisplayText(): string {
		// Helper to get selected options based on values
		const getSelectedOptions = (values: any[]) => values
			.map(value => this.options?.find(opt =>
				this.optionValueKey ? opt[this.optionValueKey] === value : opt === value
			))
			.filter(opt => opt != null);

		// Helper to get option label
		const getOptionLabel = (option: any) => option?.[this.optionLabelKey] || option?.label || option?.name || option?.[this.optionValueKey] || option?.id || option || '';

		let selectedValue = this.form?.get(this.controlName)?.value ??
			(this.multiple ? this.selectedOption?.map(opt => opt?.[this.optionValueKey] || opt) || []
				: this.selectedOption?.[this.optionValueKey] || this.selectedOption || null);

		if (selectedValue === null) {
			return '';
		}

		if (this.multiple && Array.isArray(selectedValue)) {
			const selectedOptions = getSelectedOptions(selectedValue);
			return selectedOptions.map(getOptionLabel).join(', ');
		} else if (!this.multiple && !Array.isArray(selectedValue)) {
			const selectedOption = this.options?.find(opt =>
				this.optionValueKey ? opt[this.optionValueKey] === selectedValue : opt === selectedValue
			);
			return getOptionLabel(selectedOption);
		}

		return '';
	}

	onKey(value) {
		this.searchString = value;
	}

	onOpenChange(opened: boolean) {
		this.openedChange.emit(opened);

		if (!opened) {
			// When panel closes, update selectedOption to reflect current selection
			const currentValue = this.form?.get(this.controlName)?.value;
			this.updateSelectedOption(currentValue);
		}

		if (this.showSearch && this.searchInput?.nativeElement) {
			if (opened) {
				// When opening, focus on search if there's no current value
				if (!this.currentValue) {
					setTimeout(() => {
						this.searchInput?.nativeElement.focus();
					}, 100);
				}
			} else {
				// When closing, clear the search string with a delay to avoid interfering with selection
				setTimeout(() => {
					this.searchString = '';
					this.searchInput.nativeElement.value = '';
				}, 200);
			}
		}
	}
}
